import { Editor } from "@tldraw/tldraw";
import { createShapesPropsFormat } from "../types";
import { useStore } from "store";
import { StoreType } from "store/types";

// Guard to prevent rotateUserElements from running multiple times per orientation change
let orientationChangeInProgress = false;

// System shape types that should NOT be rotated or repositioned during orientation change
const SYSTEM_SHAPE_TYPES = new Set([
  "format",
  "customLabel",
  "redLine",
  "blackLine",
  "dash-border",
  "foldLine",
  "doorHanger",
  "custom-box",
  "format-A6",
]);

/**
 * Rotate + reposition user elements during orientation change.
 *
 * Step 1: Save each shape's manual rotation, then normalize all to 0°
 * Step 2: Reposition using fractional center mapping (all at 0°, math is clean)
 * Step 3: Rotate if target differs from default, adding back manual rotation
 */
function rotateUserElements(
  editor: Editor,
  isPortrait: boolean,
  centerX: number,
  centerY: number,
  displayWidth: number,
  displayHeight: number,
  FIX_GAP: number,
  oldCanvas: { frontX: number; frontY: number; frontW: number; frontH: number; backX: number; backY: number }
) {
  const oldW = oldCanvas.frontW;
  const oldH = oldCanvas.frontH;
  const oldFrontX = oldCanvas.frontX;
  const oldFrontY = oldCanvas.frontY;
  const oldBackX = oldCanvas.backX;
  const oldBackY = oldCanvas.backY;

  const newW = displayWidth;
  const newH = displayHeight;
  const newFrontX = centerX - newW / FIX_GAP;
  const newFrontY = centerY;
  const newBackX = centerX + newW / FIX_GAP;
  const newBackY = centerY;

  const userShapes = editor.getCurrentPageShapes().filter((shape: any) => !SYSTEM_SHAPE_TYPES.has(shape.type));

  if (userShapes.length === 0) return;

  if (orientationChangeInProgress) return;
  orientationChangeInProgress = true;
  (useStore.getState() as StoreType).setIsOrientationChanging(true);

  try {
    // Rotation delta: P→L = -90°, L→P = +90°
    const rotationDelta = isPortrait ? Math.PI / 2 : -Math.PI / 2;

    // Old canvas page centers (for front/back detection)
    const oldFrontCX = oldFrontX + oldW / 2;
    const oldFrontCY = oldFrontY + oldH / 2;
    const oldBackCX = oldBackX + oldW / 2;
    const oldBackCY = oldBackY + oldH / 2;

    // ── STEP 1: Save original rotations, then normalize to 0° ──
    // Use rotateShapesBy with single-element arrays (each shape rotates around its OWN center).
    // No editor.select() — that creates undo marks which break single-undo grouping.
    const originalRotations = new Map<string, number>();
    for (const shape of userShapes) {
      const s = shape as any;
      const currentRotation = s.rotation ?? 0;
      if (Math.abs(currentRotation) > 0.01) {
        originalRotations.set(s.id, currentRotation);
        editor.rotateShapesBy([s.id], -currentRotation);
      }
    }

    // ── STEP 2: Reposition using fractional center mapping ──
    // All shapes are at 0° now — center = (x + w/2, y + h/2) is exact.
    const positionUpdates: any[] = [];
    const normalizedShapes = editor.getCurrentPageShapes().filter((shape: any) => !SYSTEM_SHAPE_TYPES.has(shape.type));

    for (const shape of normalizedShapes) {
      const s = shape as any;
      const pageBounds = editor.getShapePageBounds(s.id);
      if (!pageBounds) continue;

      const cx = pageBounds.x + pageBounds.w / 2;
      const cy = pageBounds.y + pageBounds.h / 2;

      const isOnFront = Math.hypot(cx - oldFrontCX, cy - oldFrontCY) <= Math.hypot(cx - oldBackCX, cy - oldBackCY);

      const oldPageX = isOnFront ? oldFrontX : oldBackX;
      const oldPageY = isOnFront ? oldFrontY : oldBackY;
      const newPageX = isOnFront ? newFrontX : newBackX;
      const newPageY = isOnFront ? newFrontY : newBackY;

      const fracX = (cx - oldPageX) / oldW;
      const fracY = (cy - oldPageY) / oldH;

      let newFracX: number;
      let newFracY: number;
      if (!isPortrait) {
        newFracX = fracY;
        newFracY = 1 - fracX;
      } else {
        newFracX = 1 - fracY;
        newFracY = fracX;
      }

      const newCX = newPageX + newFracX * newW;
      const newCY = newPageY + newFracY * newH;

      const dx = newCX - cx;
      const dy = newCY - cy;
      positionUpdates.push({ id: s.id, type: s.type, x: s.x + dx, y: s.y + dy });
    }

    if (positionUpdates.length > 0) {
      editor.updateShapes(positionUpdates);
    }

    // ── STEP 3: Apply orientationDelta + originalRotation ──
    // Use rotateShapesBy (handles arrows/lines properly) without editor.select().
    // Final rotation = orientationDelta + originalRotation preserves manual rotation.
    const shapesToRotate = editor.getCurrentPageShapes().filter((shape: any) => !SYSTEM_SHAPE_TYPES.has(shape.type));
    for (const shape of shapesToRotate) {
      const s = shape as any;
      const originalRot = originalRotations.get(s.id) ?? 0;
      const totalDelta = rotationDelta + originalRot;
      const normalized = ((totalDelta % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      if (Math.abs(normalized) > 0.01 && Math.abs(normalized - 2 * Math.PI) > 0.01) {
        editor.rotateShapesBy([s.id], totalDelta);
      }
    }
  } finally {
    // Always release guard — even if editor operations throw
    requestAnimationFrame(() => {
      orientationChangeInProgress = false;
      (useStore.getState() as StoreType).setIsOrientationChanging(false);
    });
  }
}

export const snapshotBaseFormat: createShapesPropsFormat = (
  editor,
  isPortrait,
  centerX,
  centerY,
  displayWidth,
  displayHeight,
  frontBgAssetId,
  safetyZonePx,
  bleedsZonePx,
  logo,
  qrCode,
  existSnapshot,
  isUpdateShapesPosition,
  defaultOrientation,
  isOrientationChange
) => {
  const FIX_GAP = 1.65;

  if (
    centerX === undefined ||
    centerY === undefined ||
    displayWidth === undefined ||
    displayHeight === undefined ||
    safetyZonePx === undefined ||
    bleedsZonePx === undefined ||
    defaultOrientation === undefined
  ) {
    return;
  }

  // Capture OLD canvas state BEFORE any system shape updates (for rotation)
  let oldCanvasState: {
    frontX: number;
    frontY: number;
    frontW: number;
    frontH: number;
    backX: number;
    backY: number;
  } | null = null;
  if (existSnapshot && isOrientationChange) {
    const allShapesBefore = editor.getCurrentPageShapes();
    const oldFront = allShapesBefore.find((s: any) => s.id === "shape:front") as any;
    const oldBack = allShapesBefore.find((s: any) => s.id === "shape:back") as any;
    if (oldFront) {
      oldCanvasState = {
        frontX: oldFront.x,
        frontY: oldFront.y,
        frontW: oldFront.props?.w ?? 0,
        frontH: oldFront.props?.h ?? 0,
        backX: oldBack?.x ?? 0,
        backY: oldBack?.y ?? 0,
      };
    }
    // Only show loader + set guard if there are user shapes to rotate.
    // In empty state (no user elements), skip the loader entirely.
    const hasUserShapes = allShapesBefore.some((s: any) => !SYSTEM_SHAPE_TYPES.has(s.type));
    if (hasUserShapes && oldCanvasState) {
      // Set isOrientationChanging EARLY — before editor.run() updates boundary shapes.
      // This prevents the clamp listener in useAddressBox from firing with
      // new boundary dimensions but old address box position.
      (useStore.getState() as StoreType).setIsOrientationChanging(true);

      // Mark undo checkpoint so entire orientation change is ONE undo step.
      // All operations after this mark (editor.run + rotateUserElements) are
      // reverted together with a single Cmd+Z.
      editor.mark("orientation-change");
    }
  }

  if (existSnapshot) {
    editor.run(
      () => {
        // 🔄 Update shapes - include positions ONLY when format changes
        if (isUpdateShapesPosition) {
          // ✅ Format changed: Reset ALL positions to default (including labels)
          editor.updateShapes([
            {
              id: "shape:backLabel" as any,
              type: "customLabel",
              isLocked: false,
              x: centerX + displayWidth / FIX_GAP + displayWidth / 2 - 35,
              y: centerY - 70,
              props: {
                t: "Back",
                w: 70,
                h: 50,
                allowUpdate: true,
              },
            },
            {
              id: "shape:frontLabel" as any,
              type: "customLabel",
              isLocked: false,
              x: centerX - displayWidth / FIX_GAP + displayWidth / 2 - 35,
              y: centerY - 70,
              props: {
                t: "Front",
                w: 70,
                h: 50,
                allowUpdate: true,
              },
            },
          ]);
          // ✅ Format changed: Reset positions to default + update dimensions
          editor.updateShapes([
            {
              id: "shape:front" as any,
              type: "format",
              x: centerX - displayWidth / FIX_GAP,
              y: centerY,
              isLocked: true,
              rotation: 0,
              props: {
                w: displayWidth,
                h: displayHeight,
              },
            },
            {
              id: "shape:back" as any,
              type: "format",
              x: centerX + displayWidth / FIX_GAP,
              y: centerY,
              isLocked: true,
              props: {
                w: displayWidth,
                h: displayHeight,
              },
            },
            {
              id: "shape:frontRedLine" as any,
              type: "redLine",
              x: centerX - displayWidth / FIX_GAP,
              y: centerY,
              isLocked: true,
              rotation: 0,
              props: {
                w: displayWidth,
                h: displayHeight,
                isDoorHanger: false,
              },
            },
            {
              id: "shape:backRedLine" as any,
              type: "redLine",
              x: centerX + displayWidth / FIX_GAP,
              y: centerY,
              isLocked: true,
              rotation: 0,
              props: {
                w: displayWidth,
                h: displayHeight,
                isDoorHanger: false,
              },
            },
            {
              id: "shape:frontBlackLine" as any,
              type: "blackLine",
              x: centerX - displayWidth / FIX_GAP - bleedsZonePx / 2,
              y: centerY - bleedsZonePx / 2,
              isLocked: true,
              rotation: 0,
              props: {
                w: displayWidth + bleedsZonePx,
                h: displayHeight + bleedsZonePx,
              },
            },
            {
              id: "shape:backBlackLine" as any,
              type: "blackLine",
              x: centerX + displayWidth / FIX_GAP - bleedsZonePx / 2,
              y: centerY - bleedsZonePx / 2,
              isLocked: true,
              rotation: 0,
              props: {
                w: displayWidth + bleedsZonePx,
                h: displayHeight + bleedsZonePx,
              },
            },
            {
              id: "shape:frontBorderDashLine" as any,
              type: "dash-border",
              isLocked: true,
              x: centerX - displayWidth / FIX_GAP + safetyZonePx / 2,
              y: centerY + safetyZonePx / 2,
              props: {
                w: displayWidth - safetyZonePx,
                h: displayHeight - safetyZonePx,
                isDoorHanger: false,
              },
            },
            {
              id: "shape:backBorderDashLine" as any,
              type: "dash-border",
              isLocked: true,
              x: centerX + displayWidth / FIX_GAP + safetyZonePx / 2,
              y: centerY + safetyZonePx / 2,
              props: {
                w: displayWidth - safetyZonePx,
                h: displayHeight - safetyZonePx,
                isDoorHanger: false,
              },
            },
          ]);
        } else {
          // 🔒 Normal load: Update ONLY dimensions, preserve saved positions
          editor.updateShapes([
            {
              id: "shape:front" as any,
              type: "format",
              isLocked: true,
              rotation: 0,
              props: {
                w: displayWidth,
                h: displayHeight,
              },
            },
            {
              id: "shape:back" as any,
              type: "format",
              isLocked: true,
              props: {
                w: displayWidth,
                h: displayHeight,
              },
            },
            {
              id: "shape:frontRedLine" as any,
              type: "redLine",
              isLocked: true,
              rotation: 0,
              props: {
                w: displayWidth,
                h: displayHeight,
                isDoorHanger: false,
              },
            },
            {
              id: "shape:backRedLine" as any,
              type: "redLine",
              isLocked: true,
              rotation: 0,
              props: {
                w: displayWidth,
                h: displayHeight,
                isDoorHanger: false,
              },
            },
            {
              id: "shape:frontBlackLine" as any,
              type: "blackLine",
              isLocked: true,
              rotation: 0,
              props: {
                w: displayWidth + bleedsZonePx,
                h: displayHeight + bleedsZonePx,
              },
            },
            {
              id: "shape:backBlackLine" as any,
              type: "blackLine",
              isLocked: true,
              rotation: 0,
              props: {
                w: displayWidth + bleedsZonePx,
                h: displayHeight + bleedsZonePx,
              },
            },
            {
              id: "shape:frontBorderDashLine" as any,
              type: "dash-border",
              isLocked: true,
              props: {
                w: displayWidth - safetyZonePx,
                h: displayHeight - safetyZonePx,
                isDoorHanger: false,
              },
            },
            {
              id: "shape:backBorderDashLine" as any,
              type: "dash-border",
              isLocked: true,
              props: {
                w: displayWidth - safetyZonePx,
                h: displayHeight - safetyZonePx,
                isDoorHanger: false,
              },
            },
          ]);
        }
      },
      { ignoreShapeLock: true }
    );
  } else {
    editor.createShapes([
      {
        id: "shape:backLabel" as any,
        type: "customLabel",
        isLocked: false,
        x: centerX + displayWidth / FIX_GAP + displayWidth / 2 - 35,
        y: centerY - 70,
        props: {
          t: "Back",
          w: 70,
          h: 50,
          allowUpdate: true,
        },
      },
      {
        id: "shape:frontLabel" as any,
        type: "customLabel",
        isLocked: false,
        x: centerX - displayWidth / FIX_GAP + displayWidth / 2 - 35,
        y: centerY - 70,
        props: {
          t: "Front",
          w: 70,
          h: 50,
          allowUpdate: true,
        },
      },
      {
        id: "shape:front" as any,
        type: "format",
        x: centerX - displayWidth / FIX_GAP,
        y: centerY,
        isLocked: true,
        rotation: 0,
        props: {
          w: displayWidth,
          h: displayHeight,
        },
      },
      {
        id: "shape:back" as any,
        type: "format",
        x: centerX + displayWidth / FIX_GAP,
        y: centerY,
        isLocked: true,
        props: {
          w: displayWidth,
          h: displayHeight,
        },
      },
      {
        id: "shape:frontRedLine" as any,
        type: "redLine",
        x: centerX - displayWidth / FIX_GAP,
        y: centerY,
        isLocked: true,
        rotation: 0,
        props: {
          w: displayWidth,
          h: displayHeight,
          isDoorHanger: false,
        },
      },
      {
        id: "shape:backRedLine" as any,
        type: "redLine",
        x: centerX + displayWidth / FIX_GAP,
        y: centerY,
        isLocked: true,
        rotation: 0,
        props: {
          w: displayWidth,
          h: displayHeight,
          isDoorHanger: false,
        },
      },
      {
        id: "shape:frontBlackLine" as any,
        type: "blackLine",
        x: centerX - displayWidth / FIX_GAP - bleedsZonePx / 2,
        y: centerY - bleedsZonePx / 2,
        isLocked: true,
        rotation: 0,
        props: {
          w: displayWidth + bleedsZonePx,
          h: displayHeight + bleedsZonePx,
        },
      },
      {
        id: "shape:backBlackLine" as any,
        type: "blackLine",
        x: centerX + displayWidth / FIX_GAP - bleedsZonePx / 2,
        y: centerY - bleedsZonePx / 2,
        isLocked: true,
        rotation: 0,
        props: {
          w: displayWidth + bleedsZonePx,
          h: displayHeight + bleedsZonePx,
        },
      },
      {
        id: "shape:frontBorderDashLine" as any,
        type: "dash-border",
        isLocked: true,
        x: centerX - displayWidth / FIX_GAP + safetyZonePx / 2,
        y: centerY + safetyZonePx / 2,
        props: {
          w: displayWidth - safetyZonePx,
          h: displayHeight - safetyZonePx,
          isDoorHanger: false,
        },
      },
      {
        id: "shape:backBorderDashLine" as any,
        type: "dash-border",
        isLocked: true,
        x: centerX + displayWidth / FIX_GAP + safetyZonePx / 2,
        y: centerY + safetyZonePx / 2,
        props: {
          w: displayWidth - safetyZonePx,
          h: displayHeight - safetyZonePx,
          isDoorHanger: false,
        },
      },
    ]);
  }

  if (isUpdateShapesPosition) {
    editor.updateShapes([
      {
        id: "shape:backLabel" as any,
        type: "customLabel",
        x: centerX + displayWidth / FIX_GAP + displayWidth / 2 - 35,
        y: centerY - 70,
        props: {
          t: "Back",
          w: 70,
          h: 50,
          allowUpdate: true,
        },
      },
      {
        id: "shape:frontLabel" as any,
        type: "customLabel",
        x: centerX - displayWidth / FIX_GAP + displayWidth / 2 - 35,
        y: centerY - 70,
        props: {
          t: "Front",
          w: 70,
          h: 50,
          allowUpdate: true,
        },
      },
      {
        id: "shape:front" as any,
        type: "format",
        x: centerX - displayWidth / FIX_GAP,
        y: centerY,
        isLocked: true,
        props: {
          w: displayWidth,
          h: displayHeight,
        },
      },
      {
        id: "shape:back" as any,
        type: "format",
        x: centerX + displayWidth / FIX_GAP,
        y: centerY,
        isLocked: true,
        props: {
          w: displayWidth,
          h: displayHeight,
        },
      },
      {
        id: "shape:frontRedLine" as any,
        type: "redLine",
        x: centerX - displayWidth / FIX_GAP,
        y: centerY,
        isLocked: true,
        props: {
          w: displayWidth,
          h: displayHeight,
          isDoorHanger: false,
        },
      },
      {
        id: "shape:backRedLine" as any,
        type: "redLine",
        x: centerX + displayWidth / FIX_GAP,
        y: centerY,
        isLocked: true,
        props: {
          w: displayWidth,
          h: displayHeight,
          isDoorHanger: false,
        },
      },
      {
        id: "shape:frontBlackLine" as any,
        type: "blackLine",
        x: centerX - displayWidth / FIX_GAP - bleedsZonePx / 2,
        y: centerY - bleedsZonePx / 2,
        isLocked: true,
        props: {
          w: displayWidth + bleedsZonePx,
          h: displayHeight + bleedsZonePx,
        },
      },
      {
        id: "shape:backBlackLine" as any,
        type: "blackLine",
        x: centerX + displayWidth / FIX_GAP - bleedsZonePx / 2,
        y: centerY - bleedsZonePx / 2,
        isLocked: true,
        props: {
          w: displayWidth + bleedsZonePx,
          h: displayHeight + bleedsZonePx,
        },
      },
      {
        id: "shape:frontBorderDashLine" as any,
        type: "dash-border",
        isLocked: true,
        x: centerX - displayWidth / FIX_GAP + safetyZonePx / 2,
        y: centerY + safetyZonePx / 2,
        props: {
          w: displayWidth - safetyZonePx,
          h: displayHeight - safetyZonePx,
          isDoorHanger: false,
        },
      },
      {
        id: "shape:backBorderDashLine" as any,
        type: "dash-border",
        isLocked: true,
        x: centerX + displayWidth / FIX_GAP + safetyZonePx / 2,
        y: centerY + safetyZonePx / 2,
        props: {
          w: displayWidth - safetyZonePx,
          h: displayHeight - safetyZonePx,
          isDoorHanger: false,
        },
      },
    ]);
  }

  // Rotate + reposition user elements only when orientation changes (not format change)
  if (isOrientationChange && oldCanvasState) {
    rotateUserElements(
      editor,
      isPortrait ?? false,
      centerX,
      centerY,
      displayWidth,
      displayHeight,
      FIX_GAP,
      oldCanvasState
    );
  }
};
